/**
 * The partner-referral stash — the single signal that says "this prospect came
 * in through a partner link and owes the promo gate".
 *
 * Everything about the Fireworks flow keys off this one localStorage entry:
 * whether the challenge cards are skipped, whether the mandatory promo step is
 * shown, and which partner's name the step is branded with. So it has exactly
 * three rules:
 *
 * 1. **It holds partner identity, never a promo code.** The prospect proves
 *    they were given a code by typing it; a code we'd already parked in
 *    localStorage is one devtools tab away from being handed back to them.
 *    Nothing here can be redeemed.
 * 2. **Only ever stash a partner the backend confirmed.** A `?promo=` value is
 *    attacker- and typo-controlled. The gate has no Skip button, so stashing an
 *    unverified value would let `?promo=whatever` park a real prospect on a card
 *    no code they own can open. Unverified in → nothing stashed → completely
 *    normal onboarding.
 * 3. **Clear it on every terminal path.** A referral that outlives its own
 *    signup would gate the next, unrelated onboarding in the same browser.
 *
 * It lives in localStorage rather than the URL because it has to survive the
 * OAuth round-trip, which drops query params.
 */

import apiUrl from '@/apis/api';

/** Verified partner identity. This is the key the gate reads. */
export const PROMO_STASH_KEY = 'agent_insights_promo';

/**
 * Raw, unverified slug straight off the URL — a note to self, with no authority.
 *
 * Capture and verification are split because they happen at different moments.
 * Capture must be SYNCHRONOUS: the entry link redirects immediately and the
 * signup page's OAuth buttons are clickable the instant it renders, so anything
 * waiting on a network round-trip can be navigated away from and lost.
 * Verification then happens once, awaited, at the only point that cares —
 * `resolvePendingPromo` before the gate decision.
 *
 * Nothing reads this key to decide whether to gate, so a hostile `?promo=` sits
 * here inert until the backend either confirms it (promoting it to
 * PROMO_STASH_KEY) or discards it.
 */
export const PROMO_PENDING_KEY = 'agent_insights_promo_pending';

/**
 * Partner IDENTITY only.
 *
 * Deliberately holds no promo code. The code is what the prospect has to prove
 * they were given, so it must not be sitting in localStorage where opening
 * devtools hands it over — and it never arrives here anyway, because the
 * lookup below resolves public slugs and the backend never returns a code.
 */
export interface PartnerPromo {
  /** Backend-resolved partner slug, stored as the org's referral source. */
  partner: string;
  /** Backend-supplied display name for UI ("Fireworks") — never a guess. */
  label: string;
}

/**
 * Ask the backend whether a partner slug from the link is real.
 *
 * Public endpoint — signup calls it while logged out. It answers "is this a
 * partner?" and nothing else: it cannot confirm promo codes, so it can't be
 * used to guess one. Never throws; a failed lookup returns null, which degrades
 * the prospect to normal onboarding rather than arming a gate we couldn't
 * verify.
 *
 * Args:
 *   slug: Raw partner slug from the URL (`/partner/<slug>` or `?promo=`).
 *
 * Returns:
 *   The resolved partner identity, or null if unrecognized/unreachable.
 */
export const lookupPartnerPromo = async (
  slug: string
): Promise<PartnerPromo | null> => {
  const trimmed = slug.trim();
  if (!trimmed) return null;

  try {
    const r = await fetch(
      `${apiUrl}agentInsightsPromoPartner/?slug=${encodeURIComponent(trimmed)}`
    );
    if (!r.ok) return null;

    const data = (await r.json()) as {
      valid?: boolean;
      partner?: string | null;
      displayName?: string | null;
    };
    if (!data.valid || !data.partner) return null;

    return {
      partner: data.partner,
      label: data.displayName || data.partner,
    };
  } catch {
    // Lookup unavailable — treat as "not a partner" and leave onboarding alone.
    return null;
  }
};

// localStorage is not guaranteed to work just because `window` exists — Safari
// private mode, disabled storage and quota-exceeded all THROW on access rather
// than returning null. Every access in this module goes through these, because
// the callers are mid-navigation or mid-onboarding: an exception escaping here
// strands a prospect on a blank redirect page or breaks the step transition.
// Losing the referral is the acceptable failure; blocking the person is not.

const safeGet = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage unavailable — the prospect continues as organic, unattributed.
  }
};

const safeRemove = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch {
    // Nothing to do: if we can't write, there's nothing stashed to clear.
  }
};

/** Read the stashed partner referral, or null if there isn't a usable one. */
export const readPromoStash = (): PartnerPromo | null => {
  if (typeof window === 'undefined') return null;

  const raw = safeGet(PROMO_STASH_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<PartnerPromo>;
    if (!parsed?.partner) return null;
    return { partner: parsed.partner, label: parsed.label || parsed.partner };
  } catch {
    // Corrupt or legacy plain-string value — not something we can gate on.
    return null;
  }
};

/** True while a verified partner referral is pending its promo gate. */
export const owesPromoGate = (): boolean => readPromoStash() !== null;

/**
 * Stash a partner referral so it survives the OAuth round-trip.
 *
 * Verifies against the backend first and stashes nothing if the value isn't a
 * known partner, so callers can pass a raw `?promo=` param straight in. What
 * gets written is identity only — no redeemable code.
 *
 * Args:
 *   slug: Raw partner slug from the URL (`/partner/<slug>` or `?promo=`).
 *
 * Returns:
 *   The stashed partner, or null if the value wasn't a recognized partner.
 */
export const stashVerifiedPromo = async (
  slug: string
): Promise<PartnerPromo | null> => {
  const partner = await lookupPartnerPromo(slug);
  if (!partner || typeof window === 'undefined') return null;

  safeSet(PROMO_STASH_KEY, JSON.stringify(partner));
  return partner;
};

/**
 * Capture a slug off the URL immediately, without waiting on the network.
 *
 * Synchronous by design: the caller is usually about to navigate (entry link) or
 * about to hand the page to an OAuth click (signup), and a referral that depends
 * on a fetch resolving first is a referral that gets dropped. Grants nothing on
 * its own — `resolvePendingPromo` still has to verify it.
 *
 * Args:
 *   slug: Raw `?promo=` / `/partner/<slug>` value. Blank values are ignored.
 */
export const stashPendingPromo = (slug: string): void => {
  const trimmed = (slug || '').trim();
  if (!trimmed || typeof window === 'undefined') return;
  safeSet(PROMO_PENDING_KEY, trimmed);
};

/**
 * Verify any pending slug and promote it to the real stash. Await before gating.
 *
 * This is the single point where an unverified slug becomes a referral with
 * authority, and it must be awaited before anything calls `owesPromoGate()` —
 * otherwise the gate decision races the verification and a referred prospect
 * gets routed as organic, silently losing the attribution.
 *
 * Idempotent and cheap: with nothing pending it makes no request and just
 * reports whatever is already verified, so it's safe to call on every mount.
 *
 * Returns:
 *   The verified partner (freshly promoted or already stashed), else null.
 */
export const resolvePendingPromo = async (): Promise<PartnerPromo | null> => {
  if (typeof window === 'undefined') return null;

  const pending = safeGet(PROMO_PENDING_KEY);
  if (!pending) return readPromoStash();

  // Clear first: a slug the backend rejects must not be retried on every mount.
  safeRemove(PROMO_PENDING_KEY);
  return (await stashVerifiedPromo(pending)) ?? readPromoStash();
};

/**
 * Forget the referral — redeemed, unusable, or the prospect went elsewhere.
 *
 * Clears the pending slug too. Leaving that behind would let a spent referral
 * re-arm the gate on the next mount, which is the leak this whole lifecycle
 * exists to prevent.
 */
export const clearPromoStash = (): void => {
  if (typeof window === 'undefined') return;
  safeRemove(PROMO_STASH_KEY);
  safeRemove(PROMO_PENDING_KEY);
};
